import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import {
  CALL_HISTORY_FOR_2ND_APPS,
  GET_CATEGORY_RANKING_FOR_UTAGE_WEB,
  GET_HIGH_REVIEW_SCORE_USERS,
  GET_USERS_BY_PREFERENCE,
  UTAGE_WEB_GET_MEET_PEOPLE_EXCLUDE_VIDEO_CALL_CHANNELER,
} from "~/constants/apiEndpoints";
import { apiClient } from "~/lib/apiClient";
import { queryKeys } from "~/lib/queryKeys";
import { useAuthStore } from "~/stores/useAuthStore";
import type {
  CallHistoryResponse,
  DiscoverUserListResponse,
  HighReviewScoreUsersResponse,
  PreferenceMatchUsersResponse,
  RankedDiscoverUser,
  RecommendRankingResponse,
} from "./types";
import { toTalkedBeforeUsers } from "./utils/talkedBeforeUsers";

const PAGE_SIZE = 19;

/** さがす一覧のページ位置。`skip` は表示ログの通し順位に使う */
type DiscoverPageParam = {
  /** 次ページの起点 (last_login_time) */
  cursor?: string;
  /** そこまでに読み込み済みの件数 */
  skip: number;
};

const buildRequestBody = ({ cursor, skip }: DiscoverPageParam) => {
  return {
    limit: PAGE_SIZE,
    sort_type: 0,
    default_avatar_flag: false,
    skip,
    ...(cursor !== undefined ? { last_login_time: cursor } : {}),
  };
};

const HIGH_REVIEW_SCORE_LIMIT = 20;

export const highReviewScoreUsersQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.discover.highReviewScoreUsers(),
    queryFn: async () => {
      const users = await apiClient<HighReviewScoreUsersResponse>(
        GET_HIGH_REVIEW_SCORE_USERS,
        { limit: HIGH_REVIEW_SCORE_LIMIT },
      );
      // API によって last_login_time / last_login どちらで返るか不定なため正規化
      return users.map((u) => ({
        ...u,
        last_login_time: u.last_login_time || u.last_login || "",
      }));
    },
    // サーバー側が1時間キャッシュするため、クライアント側も同じ時間を設定
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });

// 「あなたの好みに合う子」セクション。オンボーディング（search_users_by_preference）で
// 保存済みの好み条件を使い、書き込みなしで検索だけ行う（docs/api/discover.md 参照）。
// 好み未保存のユーザーには絞り込みなし（通常一覧相当）が返る。
export const preferenceMatchUsersQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.discover.preferenceMatchUsers(),
    queryFn: async () => {
      const users = await apiClient<PreferenceMatchUsersResponse>(
        GET_USERS_BY_PREFERENCE,
        {},
      );
      // このAPIは last_login_time ではなく last_login で返るため正規化する
      // （未正規化だとオンライン判定が常に offline= 灰色になる）
      return users.map((u) => ({
        ...u,
        last_login_time: u.last_login_time || u.last_login || "",
      }));
    },
  });

const NEWCOMER_LIMIT = 50;

// 新人タブ用。検索画面と同じエンドポイント・同じ is_new フィルタで新人のみ取得する
// （検索の buildSearchRequestBody が isNew=true のとき body.is_new=true を送るのと同等）。
export const newcomerUsersQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.discover.newcomers(),
    queryFn: () =>
      apiClient<DiscoverUserListResponse>(
        UTAGE_WEB_GET_MEET_PEOPLE_EXCLUDE_VIDEO_CALL_CHANNELER,
        {
          limit: NEWCOMER_LIMIT,
          sort_type: 0,
          default_avatar_flag: false,
          is_new: true,
        },
      ),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

// ビデオ通話ランキングタブ用。本家 Utage と同じ人を出すため
// get_category_ranking_for_utage_web を使う。type:2=ビデオ通話（0=総合 / 1=ツーショット / 3=メッセージ）。
const RECOMMEND_RANKING_TYPE = 2;
const RECOMMEND_RANKING_TAKE = 50;

// ランキングのネストしたレスポンスを一覧カードが扱う DiscoverUser（順位付き）へフラット化する
const toRankedDiscoverUser = (
  item: RecommendRankingResponse[number],
): RankedDiscoverUser => ({
  user_id: item.user_id,
  ava_id: item.user.ava_id || null,
  abt: item.user.abt ?? "",
  age: item.user.age,
  user_name: item.user.user_name,
  region: item.user.region,
  voice_call_waiting: item.user.voice_call_waiting,
  video_call_waiting: item.user.video_call_waiting,
  is_new_user: item.user.is_new,
  is_calling: false,
  is_live_now: false,
  last_login_time: item.user.last_login_time,
  has_story_movie: false,
  step_to_call: 0,
  has_lovense: item.has_lovense,
  is_listed_on_flea_market: item.user.is_listed_on_flea_market ?? false,
  average_score: item.average_score ?? undefined,
  review_count: item.review_count,
  rank: item.rank,
});

export const recommendRankingQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.discover.recommendRanking(),
    queryFn: async () => {
      const data = await apiClient<RecommendRankingResponse>(
        GET_CATEGORY_RANKING_FOR_UTAGE_WEB,
        {
          type: RECOMMEND_RANKING_TYPE,
          skip: 0,
          take: RECOMMEND_RANKING_TAKE,
        },
      );
      return data.map(toRankedDiscoverUser);
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

export const discoverUsersInfiniteQueryOptions = () =>
  infiniteQueryOptions({
    queryKey: queryKeys.discover.list(),
    queryFn: ({ pageParam }) =>
      apiClient<DiscoverUserListResponse>(
        UTAGE_WEB_GET_MEET_PEOPLE_EXCLUDE_VIDEO_CALL_CHANNELER,
        buildRequestBody(pageParam),
      ),
    initialPageParam: { skip: 0 } as DiscoverPageParam,
    // 1 ページが PAGE_SIZE 未満で返ることがある（サーバーがビデオ通話チャンネラーを除外する）。
    // 終端はカーソルの有無で判定し、skip は実際に受け取った件数を積む
    getNextPageParam: (lastPage, allPages) => {
      const cursor = lastPage.at(-1)?.last_login_time;
      return cursor === undefined
        ? undefined
        : { cursor, skip: allPages.reduce((n, page) => n + page.length, 0) };
    },
  });

// 「前に話した女の子」セクション。通話履歴から通話相手だけを新しい順に出す。
// live / side_watch（配信視聴）は「見ただけ」なので含めない。BE の 100 件上限は
// call_type ごとに効くため、種別を分けて投げると配信視聴の履歴で通話相手が
// 押し出されない（docs/api/discover.md 参照）。
const TALKED_BEFORE_CALL_TYPES = ["video", "voice"] as const;
const TALKED_BEFORE_LIMIT = 20;
// call_history_for_2nd_apps の gender は「リクエスト元（自分）の性別」。相手の性別ではない
const REQUESTER_GENDER_MALE = 0;

export const talkedBeforeUsersQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.discover.talkedBeforeUsers(),
    queryFn: async () => {
      const userId = useAuthStore.getState().userId ?? "";
      const results = await Promise.all(
        TALKED_BEFORE_CALL_TYPES.map((callType) =>
          apiClient<CallHistoryResponse>(CALL_HISTORY_FOR_2ND_APPS, {
            user_id: userId,
            gender: REQUESTER_GENDER_MALE,
            call_type: callType,
          }),
        ),
      );
      return toTalkedBeforeUsers(
        results.flatMap((items) => items ?? []),
        TALKED_BEFORE_LIMIT,
      );
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
